import { API, API_BLOB } from '.'
import type { BlobResponse } from '.'
import { API_BASE_URL } from '@/config'
import type { UserDataExportDTO } from './dto/gdpr.dto'

/* --------------------------------------------- */
/*      GDPR USER DATA EXPORT API CALLS          */
/* --------------------------------------------- */

export function requestUserDataExport() {
  return API<UserDataExportDTO>(
    '/settings/user-data-export',
    { method: 'POST' },
    'Fehler beim Anfordern des Datenexports',
    'USER_DATA_EXPORT_REQUEST_ERROR'
  )
}

/**
 * No {id} param - backend resolves the current user's (latest) export.
 * Despite the Swagger schema saying "string", the response body is the raw
 * export file (ZIP) - download it as a blob.
 *
 * The endpoint's content negotiation only accepts application/json (it returns
 * 406 for Accept: application/octet-stream, even though it writes raw bytes), so
 * the default Accept header from API_BLOB must be overridden here.
 */
export function downloadUserDataExport(): Promise<BlobResponse> {
  return API_BLOB(
    '/settings/user-data-export/download',
    { method: 'GET', headers: { Accept: '*/*' } },
    'Fehler beim Herunterladen des Datenexports',
    'USER_DATA_EXPORT_DOWNLOAD_ERROR'
  )
}

type UserDataExportStatusHandlers = {
  onEvent: (event: UserDataExportDTO) => void
  onError?: (error: Event) => void
}

const STATUS_POLL_INTERVAL_MS = 3000

/**
 * GET /settings/user-data-export/status pushes a single named "status" SSE event
 * (not the default unnamed "message" event) and then ends the response - it is a
 * one-shot snapshot, not a persistent stream. We deliberately close the connection
 * the moment we receive an event, and re-open it ourselves on a short interval while
 * the export is still PENDING/PROCESSING, stopping once it reaches a terminal state
 * (COMPLETED/FAILED). This avoids relying on the browser's built-in auto-reconnect,
 * which otherwise fires noisy "error" events in a loop once the server closes its end.
 * Returns a cleanup function that stops polling and closes any open connection.
 */
export function subscribeToUserDataExportStatus({
  onEvent,
  onError,
}: UserDataExportStatusHandlers): () => void {
  let stopped = false
  let source: EventSource | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const connect = () => {
    if (stopped) return

    source = new EventSource(`${API_BASE_URL}/settings/user-data-export/status`, {
      withCredentials: true,
    })

    const handleEvent = (event: MessageEvent) => {
      source?.close()

      try {
        const data = JSON.parse(event.data) as UserDataExportDTO
        onEvent(data)

        if (!stopped && (data.status === 'PENDING' || data.status === 'PROCESSING')) {
          retryTimer = setTimeout(connect, STATUS_POLL_INTERVAL_MS)
        }
      } catch (e) {
        console.error('Failed to parse user-data-export status event', e)
      }
    }

    source.addEventListener('status', handleEvent)
    source.onmessage = handleEvent

    source.onerror = e => {
      if (stopped) return
      onError?.(e)
    }
  }

  connect()

  return () => {
    stopped = true
    if (retryTimer) clearTimeout(retryTimer)
    source?.close()
  }
}
